import type { ReactNode } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button, ItemIcon, ListContentLoader } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text, TruncatedText } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

const { carbon } = colorsPalettes;

export const CUSTOM_CATEGORY_MODAL_WIDTH = "574px";
// Matches ListEditModal's entities box, so switching to the editor keeps the dialog's height.
const ENTITIES_BOX_HEIGHT = "324px";

interface ICustomCategoryReadOnlyItem {
    name: string;
    favicon?: string;
    subName?: string;
    id?: string;
}

interface ICustomCategoryReadOnlyViewProps {
    categoryName: string;
    items: ICustomCategoryReadOnlyItem[];
    listTitle: string;
    listCounterText: string;
    listLoadingText: string;
    isListLoading: boolean;
    onEdit: VoidFunction;
    onConfirm: VoidFunction;
    countryComp?: ReactNode;
}

/** The industry's name, country and entities without any editing affordance, laid out like the editor. */
export const CustomCategoryReadOnlyView = ({
    categoryName,
    items,
    listTitle,
    listCounterText,
    listLoadingText,
    isListLoading,
    onEdit,
    onConfirm,
    countryComp,
}: ICustomCategoryReadOnlyViewProps) => (
    <Stack
        width={CUSTOM_CATEGORY_MODAL_WIDTH}
        padding="16px 24px"
        boxSizing="border-box"
        data-automation="custom-category-read-only"
    >
        <TruncatedText
            fontVariant="input1"
            color={carbon[500]}
            paddingY="4px"
            margin="0 0 10px"
            lineClamp={1}
            title={categoryName}
            data-automation="custom-category-read-only-name"
        >
            {categoryName}
        </TruncatedText>
        {countryComp}
        <Flex
            justifyContent="space-between"
            margin="16px 0 4px"
        >
            <Text
                fontVariant="body2"
                color={carbon[300]}
            >
                {listTitle}
            </Text>
            <Text
                fontVariant="body2"
                color={carbon[300]}
            >
                {listCounterText}
            </Text>
        </Flex>
        <Box
            height={ENTITIES_BOX_HEIGHT}
            border={`1px solid ${carbon[200]}`}
            borderRadius="4px"
            boxSizing="border-box"
            overflowY="auto"
        >
            {isListLoading ? (
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                >
                    <ListContentLoader loaderSubtitle={listLoadingText} />
                </Flex>
            ) : (
                items.map((item) => (
                    <Flex
                        key={`item-${item.name}-${item.id}`}
                        alignItems="center"
                        gap="8px"
                        minHeight="40px"
                        marginX="15px"
                        paddingX="16px"
                        borderBottom={`1px solid ${carbon[50]}`}
                        boxSizing="border-box"
                        data-automation="custom-category-read-only-item"
                    >
                        {item.favicon ? <ItemIcon iconSrc={item.favicon} /> : null}
                        <Stack>
                            <Text
                                fontVariant="input2"
                                color={carbon[500]}
                            >
                                {item.name}
                            </Text>
                            {item.subName ? (
                                <Text
                                    fontVariant="listItem3"
                                    color={carbon[300]}
                                >
                                    {item.subName}
                                </Text>
                            ) : null}
                        </Stack>
                    </Flex>
                ))
            )}
        </Box>
        <Flex
            justifyContent="flex-end"
            gap="5px"
            margin="22px 0 0"
        >
            <Button
                type="flat"
                onClick={onEdit}
                dataAutomation="custom-category-read-only-edit"
            >
                {translate("global.edit")}
            </Button>
            <Button
                type="primary"
                onClick={onConfirm}
                dataAutomation="custom-category-read-only-ok"
            >
                {translate("customcategories.wizard.ok.button")}
            </Button>
        </Flex>
    </Stack>
);
